/**
 * SEG (Standard Email Generation) Service
 * Handles SEG-related API calls
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse, Seg } from '@/types';

interface SegResponseDto {
  _id: string;
  sop_id: string;
  accountId: number;
  version: string;
  status: boolean;
  title: string;
  description: string;
  category: string;
  intents: string[];
  tags: string[];
  header: Seg['header'];
  nodes: Seg['nodes'];
  tools_used: string[];
  node_types: string[];
  triggeredAt: string | null;
  trigger_count: number;
  created_at: string;
  updated_at: string;
}

function mapSegFromDto(dto: SegResponseDto): Seg {
  return {
    id: dto._id,
    sop_id: dto.sop_id,
    accountId: dto.accountId,
    version: dto.version,
    status: dto.status,
    title: dto.title,
    description: dto.description,
    category: dto.category,
    intents: dto.intents || [],
    tags: dto.tags || [],
    header: dto.header,
    nodes: dto.nodes || [],
    tools_used: dto.tools_used || [],
    node_types: dto.node_types || [],
    triggeredAt: dto.triggeredAt,
    trigger_count: dto.trigger_count ?? 0,
    created_at: dto.created_at,
    updated_at: dto.updated_at
  };
}

export const segService = {
  async getSegById(id: string): Promise<Seg> {
    const response = await api.get<ApiResponse<SegResponseDto>>(API_ENDPOINTS.SEGS.BY_ID(id));
    return mapSegFromDto(response.data.data);
  },

  async toggleSegStatus(id: string): Promise<Seg> {
    const response = await api.patch<ApiResponse<SegResponseDto>>(API_ENDPOINTS.SEGS.TOGGLE_STATUS(id));
    return mapSegFromDto(response.data.data);
  },

  async updateSeg(id: string, data: Record<string, unknown>): Promise<Seg> {
    const response = await api.put<ApiResponse<SegResponseDto>>(API_ENDPOINTS.SEGS.BY_ID(id), data);
    return mapSegFromDto(response.data.data);
  }
};
